/** Мелкие переиспользуемые элементы интерфейса. */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-10 text-ink-400">
      <span
        className="h-5 w-5 animate-spin rounded-full border-2 border-ink-600 border-t-amber-400"
        aria-hidden
      />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

/** Плейсхолдер на время загрузки: заметно приятнее пустого экрана. */
export function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`relative overflow-hidden rounded-xl bg-ink-800 ${className}`}>
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-ink-700/60 to-transparent" />
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="panel flex flex-col items-center gap-3 px-6 py-14 text-center">
      <h3 className="font-sans text-2xl text-ink-100">{title}</h3>
      {description && <p className="max-w-md text-sm text-ink-400">{description}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="panel flex flex-col items-center gap-3 border-rust/40 px-6 py-10 text-center">
      <p className="text-sm text-rust">{message}</p>
      {onRetry && (
        <button type="button" className="btn-secondary" onClick={onRetry}>
          {t("common.retry")}
        </button>
      )}
    </div>
  );
}

export function Tag({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <span className={`chip ${className}`}>{children}</span>;
}

/** Полоса прогресса очков относительно максимума раунда. */
export function ScoreBar({ value, max, className = "" }: { value: number; max: number; className?: string }) {
  const percent = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;

  return (
    <div className={`h-2 w-full overflow-hidden rounded-full bg-ink-800 ${className}`}>
      <div
        className="h-full rounded-full bg-gradient-to-r from-amber-600 to-amber-300 transition-[width] duration-700 ease-out"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}
